import { TFunction, useT } from '@transifex/react'
import { useMemo } from 'react'
import {
  isLayby,
  isLegacyServiceSale,
  isMarkedAsUnfulfilled,
  isOnAccount,
  isUnfulfilledLineItem,
} from '../../../../functions/sale'
import { hasServiceOrder } from '../../../../functions/service'
import { useAppSelector } from '../../../../state/hooks'
import {
  selectCustomFields,
  selectLineItemFulfilmentType,
  selectSaleStatus,
  selectServiceFields,
} from '../../../../state/slices/currentSale/currentSaleSlice'

export type LineItemLockedTooltipVariant = 'default' | 'fulfilled'

export interface LineItemLockedIconProps {
  lineItemID: string
  tooltipVariant?: LineItemLockedTooltipVariant
}

export interface LineItemLockedIconModel {
  tooltipInfo: { title?: string; message: string } | null
}

const getFulfilledTooltipMessage = (t: TFunction) =>
  t('Fulfilled items can’t be removed.\nCreate a return instead.', {
    _context: 'Tooltip on locked line item that has already been fulfilled',
  })

const getLockedTooltipMessages = (t: TFunction) => ({
  SERVICE_ORDER: {
    title: t('Service sale'),
    message: t(
      'This item can’t be removed from the sale because it is included in the service.',
      { _context: 'Tooltip on locked line item when sale is a service' }
    ),
  },
  ONACCOUNT: {
    title: t('Sale is on account'),
    message: t(
      'This item can’t be removed from the sale because it’s on account.',
      { _context: 'Tooltip on locked line item when sale is on account' }
    ),
  },
  LAYBY: {
    title: t('Sale is on layaway'),
    message: t(
      'This item can’t be removed from the sale because it’s on layaway.',
      { _context: 'Tooltip on locked line item when sale is on layaway' }
    ),
  },
  UNFULFILLED: {
    title: t('Sale has items for fulfillment'),
    message: t(
      'This item can’t be removed from the sale because it requires fulfillment.',
      {
        _context: 'Tooltip on locked line item as it is a fulfillment item',
      }
    ),
  },
  TAKE_NOW: {
    title: t('Sale has items for fulfillment'),
    message: t(
      'This item can’t be removed from the sale because there are items in this sale for fulfillment.',
      {
        _context:
          'Tooltip on locked line item when sale has items for fulfillment',
      }
    ),
  },
})

export const useLineItemLockedIconModel = ({
  lineItemID,
  tooltipVariant = 'default',
}: LineItemLockedIconProps): LineItemLockedIconModel => {
  const t = useT()
  const messages = useMemo(() => getLockedTooltipMessages(t), [t])
  const fulfilledMessage = useMemo(() => getFulfilledTooltipMessage(t), [t])
  const status = useAppSelector(selectSaleStatus)
  const serviceFields = useAppSelector(selectServiceFields)
  const customFields = useAppSelector(selectCustomFields)
  const lineItemFulfilmentType = useAppSelector(
    selectLineItemFulfilmentType(lineItemID)
  )

  // Items rendered under the FULFILLED banner (already fulfilled or taken
  // home) get the new "create a return" tooltip. The caller owns this decision.
  if (tooltipVariant === 'fulfilled') {
    return { tooltipInfo: { message: fulfilledMessage } }
  }

  if (
    serviceFields &&
    (hasServiceOrder(serviceFields, customFields) ||
      isLegacyServiceSale(status))
  ) {
    return { tooltipInfo: messages.SERVICE_ORDER }
  }

  if (isOnAccount(status)) {
    return { tooltipInfo: messages.ONACCOUNT }
  }

  if (isLayby(status)) {
    return { tooltipInfo: messages.LAYBY }
  }

  if (isUnfulfilledLineItem({ fulfilmentType: lineItemFulfilmentType })) {
    return { tooltipInfo: messages.UNFULFILLED }
  }

  if (isMarkedAsUnfulfilled(status)) {
    // Take now item, when continuing a sale with fulfillment line item
    return { tooltipInfo: messages.TAKE_NOW }
  }

  return { tooltipInfo: null }
}
